import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  ServerStackIcon,
  DevicePhoneMobileIcon,
  WifiIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useManagement } from '../context/ManagementContext';
import managementService from '../api/managementService';
import { useTranslation } from 'react-i18next';

// Helper function to calculate fetch progress percentage
const calculateFetchProgress = (timestamp: string | null): number => {
  if (!timestamp) return 0;

  // Start of 2025 (0%)
  const startOf2025 = new Date('2025-01-01T00:00:00.000Z').getTime();
  // Current time (100%)
  const now = Date.now();
  // Timestamp to calculate
  const fetchTime = new Date(timestamp).getTime();

  // Calculate percentage
  const totalRange = now - startOf2025;
  const progressRange = fetchTime - startOf2025;

  // Ensure percentage is between 0 and 100
  return Math.max(0, Math.min(100, (progressRange / totalRange) * 100));
};

// Helper function to get gateway sync status
const getGatewaySyncStatus = (gateway: any) => {
  const energyProgress = calculateFetchProgress(gateway.lastEnergyFetchTimestamp);
  const waterProgress = calculateFetchProgress(gateway.lastWaterFetchTimestamp);
  const overallProgress = Math.max(energyProgress, waterProgress);
  return { energyProgress, waterProgress, overallProgress, isComplete: overallProgress >= 99.5 };
};

export default function DashboardHome() {
  const { t } = useTranslation();
  const { dispatch } = useManagement();

  const { data: statistics, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['statistics'],
    queryFn: () => managementService.management.getStatistics(),
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: 1,
  });

  const { data: gateways = [], isLoading: gatewaysLoading } = useQuery({
    queryKey: ['gateways'],
    queryFn: () => managementService.gateway.getAll(),
    select: (data) => data?.data || [],
    retry: 1,
  });

  const { data: customers = [], isLoading: customersLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: () => managementService.customer.getAll({ limit: 5 }),
    select: (data) => data?.data || [],
  });

  const { data: recentAssets = [], isLoading: assetsLoading } = useQuery({
    queryKey: ['recent-assets'],
    queryFn: () => managementService.asset.getAll({ limit: 5 }),
    select: (data) => data?.data || [],
  });

  const { data: unregisteredGateways = [], isLoading: unregisteredLoading } = useQuery({
    queryKey: ['unregistered-gateways'],
    queryFn: () => managementService.gateway.getAll({
      status: 'unregistered',
      limit: 10
    }),
    select: (data) => data?.data || [],
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Debug log
  console.log('Unregistered gateways:', unregisteredGateways);

  useEffect(() => {
    if (statistics) {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [statistics, dispatch]);

  // Add error logging (after all hooks)
  if (statsError) {
    console.error('Statistics error:', statsError);
  }

  // Error handling for component
  if (statsError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Dashboard</h1>
          <p className="text-gray-600 mb-4">
            Failed to load statistics: {statsError.message}
          </p>
          <button
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (statsLoading || customersLoading || assetsLoading || unregisteredLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">{t('dashboard.loadingDashboard')}</span>
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      name: t('dashboard.totalCustomers'),
      value: statistics?.totalCustomers || 0,
      active: statistics?.activeCustomers || 0,
      icon: BuildingOfficeIcon,
      color: 'bg-blue-500',
      href: '/management/customers',
    },
    {
      name: t('dashboard.totalAssets'),
      value: statistics?.totalAssets || 0,
      active: statistics?.activeAssets || 0,
      icon: ServerStackIcon,
      color: 'bg-green-500',
      href: '/management/assets',
    },
    {
      name: t('dashboard.totalDevices'),
      value: statistics?.totalDevices || 0,
      active: statistics?.activeDevices || 0,
      icon: DevicePhoneMobileIcon,
      color: 'bg-purple-500',
      href: '/management/devices',
    },
    {
      name: t('dashboard.totalGateways'),
      value: statistics?.totalGateways || 0,
      active: statistics?.activeGateways || 0,
      icon: WifiIcon,
      color: 'bg-orange-500',
      href: '/management/gateways',
    },
  ];

  const quickActions = [
    {
      name: t('dashboard.addCustomer'),
      description: t('dashboard.addCustomerDesc'),
      icon: BuildingOfficeIcon,
      color: 'bg-blue-500',
      href: '/management/customers?action=create',
    },
    {
      name: t('dashboard.addAsset'),
      description: t('dashboard.addAssetDesc'),
      icon: ServerStackIcon,
      color: 'bg-green-500',
      href: '/management/assets?action=create',
    },
    {
      name: t('dashboard.addDevice'),
      description: t('dashboard.addDeviceDesc'),
      icon: DevicePhoneMobileIcon,
      color: 'bg-purple-500',
      href: '/management/devices?action=create',
    },
  ];

  // Filter gateways that are not 100% synced
  const incompleteSyncGateways = gateways.filter((gateway) => {
    const syncStatus = getGatewaySyncStatus(gateway);
    return !syncStatus.isComplete;
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{t('dashboard.errorLoading')}</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{t('dashboard.errorLoadingDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('dashboard.welcome')}</h2>
              <p className="text-lg text-gray-600 max-w-2xl">
                {t('dashboard.welcomeDesc')}
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                <WifiIcon className="h-12 w-12 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="group relative overflow-hidden rounded-xl bg-white border border-gray-200 hover:border-gray-300 transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
          >
            <div className="px-6 py-8">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${stat.color} mb-4 group-hover:scale-110 transition-transform duration-200`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{stat.name}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900">{stat.value}</span>
                    <span className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded-full">
                      {stat.active} {t('common.active').toLowerCase()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-transparent to-gray-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          </Link>
        ))}
      </div>

      {/* Unregistered Gateways Alert */}
      {(unregisteredGateways.length > 0 || true) && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-xl shadow-lg animate-pulse-subtle">
          <div className="px-6 py-6 sm:px-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Unregistered Gateways</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {unregisteredGateways.length} gateway{unregisteredGateways.length > 1 ? 's' : ''} need{unregisteredGateways.length === 1 ? 's' : ''} to be registered
                  </p>
                </div>
              </div>
              <Link
                to="/management/gateways?status=unregistered"
                className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
              >
                View All →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {unregisteredGateways.slice(0, 6).map((gateway) => (
                <div
                  key={gateway.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white border border-orange-200 hover:border-orange-300 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <WifiIcon className="h-4 w-4 text-orange-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {gateway.name || gateway.id}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{gateway.id}</p>
                    </div>
                  </div>
                  <Link
                    to={`/management/gateways?id=${gateway.id}&action=register`}
                    className="flex-shrink-0 px-2 py-1 text-xs font-medium text-orange-600 bg-orange-100 rounded-full hover:bg-orange-200 transition-colors"
                  >
                    Register
                  </Link>
                </div>
              ))}
            </div>
            {unregisteredGateways.length > 6 && (
              <div className="mt-4 text-center">
                <Link
                  to="/management/gateways?status=unregistered"
                  className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
                >
                  + {unregisteredGateways.length - 6} more unregistered gateway{unregisteredGateways.length - 6 > 1 ? 's' : ''}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gateways Syncing */}
      {!gatewaysLoading && incompleteSyncGateways.length > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl shadow-lg">
          <div className="px-6 py-6 sm:px-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <WifiIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Gateways Syncing</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {incompleteSyncGateways.length} gateway{incompleteSyncGateways.length > 1 ? 's are' : ' is'} still syncing data
                  </p>
                </div>
              </div>
              <Link
                to="/management/gateways?status=syncing"
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                See All →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {incompleteSyncGateways.slice(0, 5).map((gateway) => {
                const syncStatus = getGatewaySyncStatus(gateway);
                return (
                  <div
                    key={gateway.id}
                    className="p-4 rounded-lg bg-white border border-blue-200 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <WifiIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {gateway.name || gateway.id}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{gateway.id}</p>
                        </div>
                      </div>
                      <span className="flex-shrink-0 text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                        {Math.round(syncStatus.overallProgress)}%
                      </span>
                    </div>

                    {/* Progress bars */}
                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-600">Energy</span>
                          <span className="text-xs text-gray-500">{Math.round(syncStatus.energyProgress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${syncStatus.energyProgress}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-600">Water</span>
                          <span className="text-xs text-gray-500">{Math.round(syncStatus.waterProgress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${syncStatus.waterProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {incompleteSyncGateways.length > 5 && (
              <div className="mt-4 text-center">
                <Link
                  to="/management/gateways?status=syncing"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  + {incompleteSyncGateways.length - 5} more gateway{incompleteSyncGateways.length - 5 > 1 ? 's' : ''} syncing
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="px-6 py-6 sm:px-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">{t('dashboard.quickActions')}</h3>
            <div className="text-sm text-gray-500">{t('dashboard.quickActionsDesc')}</div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {quickActions.map((action) => (
              <Link
                key={action.name}
                to={action.href}
                className="group relative rounded-lg border border-gray-200 p-5 hover:border-gray-300 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${action.color} mb-3 group-hover:scale-110 transition-transform duration-200`}>
                    <action.icon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{action.name}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{action.description}</p>
                </div>
                <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-transparent to-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* Recent Customers */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="px-6 py-6 sm:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('dashboard.recentCustomers')}</h3>
              </div>
              <Link
                to="/management/customers"
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                {t('dashboard.viewAll')} →
              </Link>
            </div>
            {customersLoading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : customers.length > 0 ? (
              <div className="space-y-4">
                {customers.slice(0, 5).map((customer) => (
                  <div key={customer.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{customer.name}</p>
                        <p className="text-xs text-gray-500">{customer.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {customer.isActive ? (
                        <div className="flex items-center gap-1">
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          <span className="text-xs font-medium text-green-600">{t('common.active')}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">{t('common.inactive')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">{t('dashboard.noCustomersFound')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Assets */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="px-6 py-6 sm:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <ServerStackIcon className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('dashboard.recentAssets')}</h3>
              </div>
              <Link
                to="/management/assets"
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                {t('dashboard.viewAll')} →
              </Link>
            </div>
            {assetsLoading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentAssets.length > 0 ? (
              <div className="space-y-4">
                {recentAssets.slice(0, 5).map((asset) => (
                  <div key={asset.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <ServerStackIcon className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{asset.label}</p>
                        <p className="text-xs text-gray-500">{asset.additional_info}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {asset.isActive ? (
                        <div className="flex items-center gap-1">
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          <span className="text-xs font-medium text-green-600">{t('common.active')}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">{t('common.inactive')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ServerStackIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">{t('dashboard.noAssetsFound')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
